import PropTypes from 'prop-types';

const slab = (half, top, bottom, radius) => [
  `M${-half} ${top + radius}`,
  `Q${-half} ${top} ${-half + radius} ${top}`,
  `H${half - radius}`,
  `Q${half} ${top} ${half} ${top + radius}`,
  `V${bottom - radius}`,
  `Q${half} ${bottom} ${half - radius} ${bottom}`,
  `H${-half + radius}`,
  `Q${-half} ${bottom} ${-half} ${bottom - radius}`,
  'Z',
].join('');

const tapered = ({
  half, top, bottom, noseHalf, noseRun, tailHalf, tailRun,
}) => {
  const noseKnee = noseHalf + (half - noseHalf) * 0.72;
  const tailKnee = tailHalf + (half - tailHalf) * 0.72;
  return [
    `M${-noseHalf} ${top}`,
    `Q0 ${top - 4} ${noseHalf} ${top}`,
    `C${noseKnee} ${top + noseRun * 0.1} ${half} ${top + noseRun * 0.56} ${half} ${top + noseRun}`,
    `V${bottom - tailRun}`,
    `C${half} ${bottom - tailRun * 0.56} ${tailKnee} ${bottom - tailRun * 0.1} ${tailHalf} ${bottom}`,
    `Q0 ${bottom + 4} ${-tailHalf} ${bottom}`,
    `C${-tailKnee} ${bottom - tailRun * 0.1} ${-half} ${bottom - tailRun * 0.56} ${-half} ${bottom - tailRun}`,
    `V${top + noseRun}`,
    `C${-half} ${top + noseRun * 0.56} ${-noseKnee} ${top + noseRun * 0.1} ${-noseHalf} ${top}`,
    'Z',
  ].join('');
};

const across = (half, y) => `M${-half} ${y}H${half}`;

const pane = (cowlHalf, roofHalf, cowlY, roofY) => (
  `M${-cowlHalf} ${cowlY}L${-roofHalf} ${roofY}H${roofHalf}L${cowlHalf} ${cowlY}Z`
);

const lamps = (bodyHalf, frontY, rearY) => (
  <>
    {[-1, 1].map((side) => (
      <g key={side}>
        <rect className="tm-chassis__lamp tm-chassis__lamp--front" x={side * bodyHalf * 0.62 - 5} y={frontY} width="10" height="4.5" rx="2" />
        <rect className="tm-chassis__lamp tm-chassis__lamp--rear" x={side * bodyHalf * 0.68 - 4} y={rearY} width="8" height="4" rx="1.8" />
      </g>
    ))}
  </>
);

const car = (body) => {
  const {
    topEdge, bottomEdge, bodyHalf, frontAxleY, rearAxleY, maxTyreLength,
  } = body;
  const noseRun = Math.max(16, frontAxleY - topEdge - maxTyreLength * 0.15);
  const tailRun = Math.max(16, bottomEdge - rearAxleY - maxTyreLength * 0.15);
  const cowlY = frontAxleY + maxTyreLength * 0.38;
  const hipY = rearAxleY - maxTyreLength * 0.3;
  const cabinRun = Math.max(40, hipY - cowlY);
  const roofTop = cowlY + cabinRun * 0.2;
  const roofBottom = hipY - cabinRun * 0.16;
  const roofHalf = bodyHalf * 0.62;
  const mirrorY = cowlY + cabinRun * 0.12;

  return {
    exposedGear: false,
    d: tapered({
      top: topEdge,
      bottom: bottomEdge,
      half: bodyHalf,
      noseHalf: bodyHalf * 0.53,
      noseRun,
      tailHalf: bodyHalf * 0.68,
      tailRun,
    }),
    details: (
      <>
        <path className="tm-chassis__bonnet" d={slab(bodyHalf * 0.7, topEdge + 8, cowlY - 5, 7)} />
        <path className="tm-chassis__glazing tm-chassis__glazing--front" d={pane(bodyHalf * 0.76, roofHalf, cowlY, roofTop)} />
        <path className="tm-chassis__roof" d={slab(roofHalf, roofTop, roofBottom, 9)} />
        <rect className="tm-chassis__sunroof" x={-roofHalf * 0.46} y={roofTop + 10} width={roofHalf * 0.92} height={Math.max(12, roofBottom - roofTop - 20)} rx="5" />
        <path className="tm-chassis__glazing tm-chassis__glazing--rear" d={pane(bodyHalf * 0.72, roofHalf, hipY, roofBottom)} />
        <path className="tm-chassis__line" d={across(bodyHalf * 0.66, bottomEdge - tailRun * 0.55)} />
        <path className="tm-chassis__bumper" d={across(bodyHalf * 0.47, topEdge + 4)} />
        <path className="tm-chassis__bumper" d={across(bodyHalf * 0.56, bottomEdge - 4)} />
        <path className="tm-chassis__mirror" d={`M${-bodyHalf} ${mirrorY}h-8v7h8M${bodyHalf} ${mirrorY}h8v7h-8`} />
        {lamps(bodyHalf, topEdge + 7, bottomEdge - 11)}
      </>
    ),
  };
};

const truck = (body) => {
  const {
    topEdge, bottomEdge, bodyHalf, frontAxleY, maxTyreLength, beams, railHalf,
  } = body;
  const cabBottom = Math.min(frontAxleY + maxTyreLength * 0.7, topEdge + (bottomEdge - topEdge) * 0.34);
  const screenTop = topEdge + Math.max(10, (cabBottom - topEdge) * 0.2);
  const screenBottom = topEdge + Math.max(25, (cabBottom - topEdge) * 0.5);
  const railBottom = beams[beams.length - 1].y + 15;
  const tankTop = Math.min(cabBottom + 14, beams[0].y + 8);
  const tankBottom = Math.min(railBottom - 15, tankTop + maxTyreLength * 0.85);

  return {
    d: slab(bodyHalf * 0.92, topEdge, cabBottom, 11),
    flankAt: (y) => (y <= cabBottom ? bodyHalf * 0.92 : railHalf + 9),
    details: (
      <>
        <path className="tm-chassis__truck-screen" d={pane(bodyHalf * 0.77, bodyHalf * 0.7, screenTop, screenBottom)} />
        <path className="tm-chassis__cab-roof" d={slab(bodyHalf * 0.58, screenBottom + 5, cabBottom - 9, 6)} />
        <rect className="tm-chassis__sunroof" x={-bodyHalf * 0.27} y={screenBottom + 11} width={bodyHalf * 0.54} height={Math.max(10, cabBottom - screenBottom - 26)} rx="4" />
        <path className="tm-chassis__grille" d={`M${-bodyHalf * 0.46} ${topEdge + 6}H${bodyHalf * 0.46}M${-bodyHalf * 0.38} ${topEdge + 10}H${bodyHalf * 0.38}`} />
        <path className="tm-chassis__mirror" d={`M${-bodyHalf * 0.9} ${screenTop + 5}h-10v9h10M${bodyHalf * 0.9} ${screenTop + 5}h10v9h-10`} />
        <path className="tm-chassis__drive-shaft" d={`M0 ${cabBottom - 2}V${railBottom}`} />
        {beams.map((beam) => (
          <g key={`diff-${beam.y}`} className="tm-chassis__differential">
            <circle cx="0" cy={beam.y} r={Math.min(10, beam.thickness * 1.05)} />
            <circle cx="0" cy={beam.y} r={Math.min(4, beam.thickness * 0.43)} />
          </g>
        ))}
        {tankBottom > tankTop && (
          <>
            <rect className="tm-chassis__tank" x={-bodyHalf * 0.72} y={tankTop} width={bodyHalf * 0.35} height={tankBottom - tankTop} rx="6" />
            <rect className="tm-chassis__tank" x={bodyHalf * 0.37} y={tankTop} width={bodyHalf * 0.35} height={tankBottom - tankTop} rx="6" />
          </>
        )}
        <rect className="tm-chassis__rear-member" x={-railHalf - 8} y={railBottom - 4} width={(railHalf + 8) * 2} height="8" rx="3" />
        {lamps(bodyHalf, topEdge + 7, railBottom - 7)}
      </>
    ),
  };
};

const trailer = (body) => {
  const {
    topEdge, bottomEdge, bodyHalf, beams,
  } = body;
  return {
    d: slab(bodyHalf, topEdge, bottomEdge, 7),
    details: (
      <>
        <circle className="tm-chassis__kingpin" cx="0" cy={topEdge + 21} r="7.5" />
        <path className="tm-chassis__deck" d={slab(bodyHalf * 0.89, topEdge + 35, bottomEdge - 9, 5)} />
        <path className="tm-chassis__deck-lines" d={`M${-bodyHalf * 0.62} ${topEdge + 40}V${bottomEdge - 14}M0 ${topEdge + 40}V${bottomEdge - 14}M${bodyHalf * 0.62} ${topEdge + 40}V${bottomEdge - 14}`} />
        {beams.map((beam) => <circle key={beam.y} className="tm-chassis__differential" cx="0" cy={beam.y} r="7" />)}
      </>
    ),
  };
};

const generic = (body) => {
  const {
    topEdge, bottomEdge, bodyHalf, beams,
  } = body;
  return {
    d: slab(bodyHalf * 0.92, topEdge, bottomEdge, 9),
    details: (
      <>
        <path className="tm-chassis__line" d={across(bodyHalf * 0.78, topEdge + 18)} />
        <path className="tm-chassis__line" d={across(bodyHalf * 0.78, bottomEdge - 18)} />
        {beams.map((beam) => (
          <circle key={beam.y} className="tm-chassis__differential" cx="0" cy={beam.y} r={Math.min(7, beam.thickness)} />
        ))}
      </>
    ),
  };
};

const bus = (body) => {
  const {
    topEdge, bottomEdge, bodyHalf,
  } = body;
  const screenY = topEdge + 23;
  const roofTop = screenY + 8;
  const bayTop = bottomEdge - 34;
  const hatches = [0.24, 0.57].map((fraction) => roofTop + (bayTop - roofTop) * fraction);

  return {
    exposedGear: false,
    d: slab(bodyHalf, topEdge, bottomEdge, 17),
    details: (
      <>
        <path className="tm-chassis__glazing tm-chassis__glazing--front" d={pane(bodyHalf * 0.91, bodyHalf * 0.78, topEdge + 5, screenY)} />
        <path className="tm-chassis__roof" d={slab(bodyHalf * 0.87, roofTop, bayTop, 10)} />
        <path className="tm-chassis__bus-glass" d={`M${-bodyHalf + 4} ${roofTop + 3}V${bayTop - 3}M${bodyHalf - 4} ${roofTop + 3}V${bayTop - 3}`} />
        {hatches.map((y) => (
          <rect key={y} className="tm-chassis__hatch" x="-13" y={y} width="26" height="15" rx="4" />
        ))}
        <path className="tm-chassis__deck" d={slab(bodyHalf * 0.86, bayTop + 7, bottomEdge - 9, 5)} />
        {lamps(bodyHalf, topEdge + 6, bottomEdge - 11)}
      </>
    ),
  };
};

const jcb = (body) => {
  const {
    topEdge, bottomEdge, bodyHalf, frontAxleY, rearAxleY, maxTyreLength,
  } = body;
  const hoodHalf = bodyHalf * 0.42;
  const tailHalf = bodyHalf * 0.62;
  const hoodTop = topEdge + 17;
  const cabTop = frontAxleY + maxTyreLength * 0.31;
  const cabBottom = Math.max(cabTop + 34, rearAxleY - maxTyreLength * 0.68);
  const tailBottom = bottomEdge - 10;
  const d = [
    `M${-hoodHalf} ${hoodTop + 5}`,
    `Q${-hoodHalf} ${hoodTop} ${-hoodHalf + 5} ${hoodTop}`,
    `H${hoodHalf - 5}Q${hoodHalf} ${hoodTop} ${hoodHalf} ${hoodTop + 5}`,
    `V${cabTop}H${bodyHalf - 6}Q${bodyHalf} ${cabTop} ${bodyHalf} ${cabTop + 6}`,
    `V${cabBottom - 6}Q${bodyHalf} ${cabBottom} ${bodyHalf - 6} ${cabBottom}`,
    `H${tailHalf}V${tailBottom - 5}Q${tailHalf} ${tailBottom} ${tailHalf - 5} ${tailBottom}`,
    `H${-tailHalf + 5}Q${-tailHalf} ${tailBottom} ${-tailHalf} ${tailBottom - 5}`,
    `V${cabBottom}H${-bodyHalf + 6}Q${-bodyHalf} ${cabBottom} ${-bodyHalf} ${cabBottom - 6}`,
    `V${cabTop + 6}Q${-bodyHalf} ${cabTop} ${-bodyHalf + 6} ${cabTop}H${-hoodHalf}Z`,
  ].join('');

  return {
    d,
    flankAt: (y) => {
      if (y < cabTop) return hoodHalf;
      if (y > cabBottom) return tailHalf;
      return bodyHalf;
    },
    details: (
      <>
        <path className="tm-chassis__bucket" d={`M${-bodyHalf * 0.9} ${topEdge}H${bodyHalf * 0.9}L${bodyHalf * 0.78} ${topEdge + 12}H${-bodyHalf * 0.78}Z`} />
        <path className="tm-chassis__boom" d={`M${-hoodHalf - 9} ${topEdge + 10}h8V${cabTop}h-8ZM${hoodHalf + 1} ${topEdge + 10}h8V${cabTop}h-8Z`} />
        <path className="tm-chassis__glazing" d={slab(bodyHalf * 0.86, cabTop + 6, cabBottom - 6, 5)} />
        <path className="tm-chassis__boom tm-chassis__boom--rear" d={`M-9 ${cabBottom + 3}h18V${tailBottom + 12}h-18Z`} />
        <path className="tm-chassis__bucket" d={`M-18 ${tailBottom + 8}H18L13 ${bottomEdge + 5}H-13Z`} />
      </>
    ),
  };
};

const SILHOUETTE_ART = {
  generic,
  truck, trailer, car, bus, jcb,
};

const ChassisArt = ({
  silhouette, body, fill, shade,
}) => {
  const {
    d, details, flankAt, exposedGear = true,
  } = (SILHOUETTE_ART[silhouette] || truck)(body);
  const {
    beams, railHalf, topEdge,
  } = body;
  const flank = flankAt || (() => body.bodyHalf);
  const railTop = Math.max(topEdge + 8, beams[0].y - 10);
  const railBottom = beams[beams.length - 1].y + 10;

  return (
    <g className={`tm-chassis tm-chassis--${silhouette}`}>
      <path className="tm-chassis__shadow" d={d} />
      <path className="tm-chassis__shell" d={d} fill={fill} />
      <path className="tm-chassis__shade" d={d} fill={shade} />

      {exposedGear && (
        <g className="tm-chassis__gear">
          {railHalf > 0 && <path className="tm-chassis__rail" d={`M${-railHalf} ${railTop}V${railBottom}M${railHalf} ${railTop}V${railBottom}`} />}
          {beams.map((beam) => {
            const from = Math.min(beam.from, flank(beam.y) - 6);
            const span = Math.max(4, beam.to - from);
            return (
              <g key={beam.y}>
                <rect className="tm-chassis__beam" x={-beam.to} y={beam.y - beam.thickness / 2} width={span} height={beam.thickness} rx={beam.thickness / 2} />
                <rect className="tm-chassis__beam" x={from} y={beam.y - beam.thickness / 2} width={span} height={beam.thickness} rx={beam.thickness / 2} />
              </g>
            );
          })}
        </g>
      )}

      {details}
    </g>
  );
};

ChassisArt.propTypes = {
  silhouette: PropTypes.string.isRequired,
  body: PropTypes.object.isRequired,
  fill: PropTypes.string.isRequired,
  shade: PropTypes.string.isRequired,
};

export default ChassisArt;
